import { Injectable } from '@angular/core'
import { HttpResponse } from '@angular/common/http'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { debounceTime, filter, map, startWith } from 'rxjs/operators'
import { CallerService } from './caller.service'
import { PpaModel } from '../ngrx-store/app.state'
import { suggestPpas } from '../utils/ppa-name.util'

/** A PPA as the autocomplete knows it - from GET /ppas/lga/:lgaId/autocomplete. */
export interface LgaPpa {
  id: number
  slug_id: string
  name: string
  type_of_ppa: string | null
  corp_member_count: number
  /** Its addresses in this LGA. */
  addresses: string[]
}

interface LgaList {
  ppas$: BehaviorSubject<LgaPpa[] | null>
  loadedAt: number
  loading: boolean
}

/**
 * The PPAs in each LGA, for the PPA autocomplete (the profile and the add-a-PPA form). Each LGA's
 * list is loaded once (a lean response, cached on the backend too) and kept for the session, so
 * switching LGAs or reopening a form costs no request; a list older than MAX_AGE_MS is reloaded in
 * the background. Suggestions are matched in the browser (suggestPpas) - no request per keystroke,
 * for slow networks. A PPA added here goes into its LGA's list at once (add()).
 *
 * Not the /ppa page's list (the store's `ppas`): that's one filtered, sorted page of GET /ppas, and
 * the autocomplete needs every PPA in the LGA to keep people from adding one twice.
 *
 * TODO: when an LGA gets to a couple of thousand PPAs. Measure first - how many PPAs the biggest
 * LGAs have (SELECT state_lga_id, COUNT(DISTINCT ppa_id) FROM Locations GROUP BY 1 ORDER BY 2 DESC),
 * the response size, and how long suggestPpas() takes per keystroke on a cheap Android phone. Then,
 * roughly in this order:
 * - Show at most ~50 options. An empty field lists the whole LGA, and mat-autocomplete renders
 *   every option - that slows down before the matching does.
 * - Work out each PPA's name words once per list, not on every keystroke: nameSimilarity()
 *   re-tokenises every candidate each time. Keep them on the list (e.g. `tokens` next to each
 *   LgaPpa) and give ppa-name.util a version that takes them.
 * - Score only likely candidates: an index of word -> PPAs (and of each word's first letters) to
 *   pick the PPAs sharing a word with what's typed, before the fuzzy scoring.
 * - Let the browser keep the list across visits: `Cache-Control: private, max-age=300` on the
 *   backend's response (Express already sends an ETag, so a stale one is a 304).
 * - Still slow: match in a Web Worker, or above a size switch that LGA to searching on the server
 *   (debounced, from 3 letters - GET /ppas/search, filtered by LGA), keeping this for small LGAs.
 * The backend's duplicate check (ppaDedupeService.ppasNear) also loads the whole LGA on every
 * submit, so it needs the same prefiltering - by `name_key` words in SQL.
 */
@Injectable({
  providedIn: 'root',
})
export class PpaDirectoryService {
  /** How long a loaded list is used before it's reloaded (others add PPAs too). */
  static readonly MAX_AGE_MS = 10 * 60 * 1000

  /** How long typing pauses before the suggestions are worked out again. */
  static readonly SUGGEST_DEBOUNCE_MS = 150

  private lists = new Map<string, LgaList>()

  constructor(private callerService: CallerService) {}

  /** The LGA's PPAs by name: the cached list at once if there's one, then any reload. */
  ppasInLga(lgaId: string | number | null | undefined): Observable<LgaPpa[]> {
    if (!lgaId) return of([])
    const key = String(lgaId)
    let list = this.lists.get(key)
    if (!list) {
      list = { ppas$: new BehaviorSubject<LgaPpa[] | null>(null), loadedAt: 0, loading: false }
      this.lists.set(key, list)
    }
    if (!list.loading && Date.now() - list.loadedAt > PpaDirectoryService.MAX_AGE_MS) {
      this.load(key, list)
    }
    return list.ppas$.pipe(filter((ppas): ppas is LgaPpa[] => ppas !== null))
  }

  /**
   * The PPAs to suggest for what's typed, from `ppas$` - worked out again when the list changes,
   * and once typing pauses (SUGGEST_DEBOUNCE_MS).
   */
  suggestions(ppas$: Observable<LgaPpa[]>, typed$: Observable<string | null>): Observable<LgaPpa[]> {
    return combineLatest([
      ppas$,
      typed$.pipe(debounceTime(PpaDirectoryService.SUGGEST_DEBOUNCE_MS), startWith('')),
    ]).pipe(map(([ppas, typed]) => suggestPpas(ppas, typed ?? '')))
  }

  /** A PPA just added (POST /ppa's `ppa`): into the list of each LGA it's in that's loaded. */
  add(ppa: PpaModel): void {
    const locations = ppa.Locations ?? []
    for (const lgaId of new Set(locations.map((location) => location.state_lga_id))) {
      const list = this.lists.get(String(lgaId))
      const ppas = list?.ppas$.value
      if (!list || !ppas || ppas.some((listed) => listed.id === ppa.id)) continue
      const added: LgaPpa = {
        id: ppa.id,
        slug_id: ppa.slug_id ?? String(ppa.id),
        name: ppa.name,
        type_of_ppa: ppa.type_of_ppa ?? null,
        corp_member_count: 0,
        addresses: locations
          .filter((location) => location.state_lga_id === lgaId)
          .map((location) => location.address?.trim() ?? '')
          .filter(Boolean),
      }
      list.ppas$.next(
        [...ppas, added].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
      )
    }
  }

  /** Reload this LGA's list next time it's asked for - e.g. the profile may have just added a PPA. */
  forget(lgaId: string | number | null | undefined): void {
    const list = lgaId ? this.lists.get(String(lgaId)) : undefined
    if (list) list.loadedAt = 0
  }

  /** On logout. */
  clear(): void {
    this.lists.clear()
  }

  private load(key: string, list: LgaList): void {
    list.loading = true
    this.callerService.fetchLgaPpaAutocomplete(key).subscribe({
      next: (res: HttpResponse<any>) => {
        list.loading = false
        list.loadedAt = Date.now()
        list.ppas$.next(res.body?.ppas ?? [])
      },
      error: (err) => {
        // Nothing to suggest, then - the backend still checks for duplicates on submit. The next
        // ppasInLga() tries again.
        console.log('fetchLgaPpaAutocomplete err', err)
        list.loading = false
        if (list.ppas$.value === null) list.ppas$.next([])
      },
    })
  }
}
